// [phase12bz] Signal Diagnostics — readable Health Summary by default;
// raw JSON moved into a separately-collapsible Advanced Diagnostics panel
// (admin-only when an `adversyn-admin-diagnostics=true` flag is present in
// localStorage). The section header itself remains collapsed by default
// per the Phase 11A spec, so the Signal page shows nothing until the user
// expands it. When expanded, the user-facing summary is plain English —
// no raw JSON, no raw snake_case reason codes.
import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Terminal, Info } from "lucide-react";
import type {
  SignalCandidatesPayload,
  SignalProviderHealth,
} from "../../lib/signalClient";

interface Props {
  health: SignalProviderHealth | null;
  candidates: SignalCandidatesPayload | null;
}

// Plain-English translations for the candidate-pipeline reject reasons.
// Anything not in this map falls back to a deterministic Title-Case render
// so the operator never sees raw SCREAMING_SNAKE_CASE in the main UI.
const REASON_TRANSLATIONS: Record<string, string> = {
  PREMARKET_VOLUME_BELOW_100000: "Premarket volume was below 100,000 shares.",
  FLOAT_UNKNOWN: "Float data was unavailable.",
  RECENT_DILUTION_FILM_24H: "Recent dilution risk detected.",
  RECENT_DILUTION_FILING_24H: "Recent dilution risk detected.",
  SPREAD_ABOVE_100_BPS: "Spread was too wide.",
  PRICE_ABOVE_MAX: "Price was above the Signal small-account range.",
  PRICE_BELOW_MIN: "Price was below the Signal small-account floor.",
  NOT_TOP_RANKED: "Passed filters but did not rank high enough.",
  HALT_NOT_CLEAR: "Symbol was halted or had a halt event today.",
};

function prettyReason(code: string): string {
  if (code in REASON_TRANSLATIONS) return REASON_TRANSLATIONS[code];
  // Fallback: convert SCREAMING_SNAKE_CASE -> "Screaming snake case." without
  // exposing the raw code in the main UI.
  const cleaned = code
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
  return `${cleaned}.`;
}

function prettyStatus(code: string | undefined | null): string {
  if (!code) return "Unavailable";
  switch (code.toUpperCase()) {
    case "LIVE": return "Live";
    case "DEGRADED": return "Degraded";
    case "LIMITED": return "Limited";
    case "BLOCKED": return "Blocked";
    case "LIVE_PROXY": return "Live (proxy)";
    case "STALE": return "Stale";
    case "MISSING": return "Unavailable";
    case "UNAVAILABLE": return "Unavailable";
    case "EMPTY": return "No data yet";
    case "CACHE": return "Cache";
    case "ERROR": return "Error";
    default: return code;
  }
}

function fmtEastern(iso: string | undefined | null): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("en-US", {
      timeZone: "America/New_York",
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      timeZoneName: "short",
    });
  } catch {
    return iso;
  }
}

function statusTone(code: string | undefined | null): "ok" | "warn" | "bad" | "neutral" {
  if (!code) return "neutral";
  const u = code.toUpperCase();
  if (u === "LIVE" || u === "LIVE_PROXY") return "ok";
  if (u === "DEGRADED" || u === "LIMITED" || u === "STALE" || u === "CACHE") return "warn";
  if (u === "BLOCKED" || u === "MISSING" || u === "UNAVAILABLE" || u === "ERROR") return "bad";
  return "neutral";
}

function ToneBadge({ tone, children }: { tone: "ok" | "warn" | "bad" | "neutral"; children: React.ReactNode }) {
  const styles: Record<string, string> = {
    ok: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    warn: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    bad: "bg-red-500/15 text-red-300 border-red-500/30",
    neutral: "bg-slate-500/15 text-slate-300 border-slate-500/30",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-black tracking-widest uppercase border ${styles[tone]}`}
    >
      {children}
    </span>
  );
}

export default function SignalDiagnostics({ health, candidates }: Props) {
  const [open, setOpen] = useState(false);
  const [advOpen, setAdvOpen] = useState(false);
  const [adminMode, setAdminMode] = useState(false);

  // Soft admin gate: read a localStorage flag at mount. No auth context is
  // surfaced in the trading-ui, so this is a deliberate opt-in toggle the
  // operator can set in DevTools without affecting customer users.
  useEffect(() => {
    try {
      const v = window.localStorage?.getItem("adversyn-admin-diagnostics");
      if (v === "true" || v === "1") setAdminMode(true);
    } catch {
      // localStorage may be unavailable in some embedded contexts; that's fine.
    }
  }, []);

  // Derive the plain-English summary fields.
  const matrixStatus = health?.status ?? candidates?.status ?? "UNAVAILABLE";
  const matrixTone = statusTone(matrixStatus);
  const universeSize = candidates?.universe_size ?? null;
  const candidateCount = candidates?.candidate_count ?? 0;
  const asOfEt = candidates?.as_of_et || candidates?.as_of_utc || null;

  // Quotes/spread freshness from provider snapshot.
  const provSnap = candidates?.provider_snapshot ?? {};
  const quotesEntry = provSnap["quotes_spread"] || null;
  const newsEntry = provSnap["news"] || null;

  // Top reject reasons, translated.
  const rejectedCounts = candidates?.rejected_counts ?? {};
  const topRejects = Object.entries(rejectedCounts)
    .sort((a, b) => (b[1] as number) - (a[1] as number))
    .slice(0, 5);

  // Completeness: "complete enough to trust" when health.status is LIVE and
  // there are no blockers. Otherwise tell the user explicitly why not.
  const blockers = health?.blockers ?? [];
  const completeEnough =
    matrixStatus === "LIVE" &&
    (blockers.length ?? 0) === 0 &&
    candidateCount >= 0;
  const completenessTone: "ok" | "warn" | "bad" =
    matrixStatus === "LIVE" && blockers.length === 0 ? "ok" :
    matrixStatus === "BLOCKED" || matrixStatus === "ERROR" ? "bad" : "warn";

  return (
    <section
      data-testid="signal-diagnostics"
      data-state={open ? "expanded" : "collapsed"}
      className="rounded-2xl border border-[var(--card-border)] bg-[var(--card-bg)] p-4"
    >
      <header className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="flex items-center gap-2 text-left flex-1 min-w-0 text-sm font-bold tracking-wider uppercase text-[var(--app-text)]"
          data-testid="signal-diagnostics-toggle"
        >
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          <Terminal className="h-4 w-4" /> Signal Health Summary
        </button>
        <ToneBadge tone={matrixTone}>{prettyStatus(matrixStatus)}</ToneBadge>
      </header>
      {open && (
        <div
          className="mt-3 space-y-4"
          data-testid="signal-health-summary"
        >
          <p className="text-[12px] text-[var(--muted-text)] leading-snug flex items-start gap-2">
            <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
            <span>
              This summary explains the data sources and filters behind today's Signal candidates.
              Numbers are pulled from the live candidate pipeline; nothing here is a trade recommendation.
            </span>
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div data-testid="signal-health-summary-status">
              <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)] mb-1">
                Signal status
              </div>
              <div className="flex items-center gap-2">
                <ToneBadge tone={matrixTone}>{prettyStatus(matrixStatus)}</ToneBadge>
              </div>
              {health?.display_message && (
                <div className="text-[11px] text-[var(--muted-text)] mt-1">{health.display_message}</div>
              )}
            </div>

            <div data-testid="signal-health-summary-universe">
              <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)] mb-1">
                Candidate universe scanned
              </div>
              <div className="text-[18px] font-bold tabular-nums">
                {universeSize !== null && universeSize !== undefined ? universeSize.toLocaleString() : "—"}
              </div>
              <div className="text-[11px] text-[var(--muted-text)]">symbols evaluated</div>
            </div>

            <div data-testid="signal-health-summary-selected">
              <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)] mb-1">
                Candidates selected
              </div>
              <div className="text-[18px] font-bold tabular-nums">{candidateCount}</div>
              <div className="text-[11px] text-[var(--muted-text)]">passed every filter today</div>
            </div>

            <div data-testid="signal-health-summary-refresh">
              <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)] mb-1">
                Last candidate refresh
              </div>
              <div className="text-[13px] font-mono">{fmtEastern(asOfEt)}</div>
              <div className="text-[11px] text-[var(--muted-text)]">Eastern time</div>
            </div>

            <div data-testid="signal-health-summary-quotes">
              <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)] mb-1">
                Quote / spread freshness
              </div>
              <div className="flex items-center gap-2">
                <ToneBadge tone={statusTone(quotesEntry?.status)}>{prettyStatus(quotesEntry?.status)}</ToneBadge>
                {typeof quotesEntry?.age_seconds === "number" && (
                  <span className="text-[11px] text-[var(--muted-text)]">
                    {Math.max(0, Math.round(quotesEntry.age_seconds))}s old
                  </span>
                )}
              </div>
            </div>

            <div data-testid="signal-health-summary-news">
              <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)] mb-1">
                News freshness
              </div>
              <div className="flex items-center gap-2">
                <ToneBadge tone={statusTone(newsEntry?.status)}>{prettyStatus(newsEntry?.status)}</ToneBadge>
                {typeof newsEntry?.age_seconds === "number" && (
                  <span className="text-[11px] text-[var(--muted-text)]">
                    {Math.max(0, Math.round(newsEntry.age_seconds))}s old
                  </span>
                )}
              </div>
            </div>
          </div>

          {topRejects.length > 0 && (
            <div data-testid="signal-health-summary-rejects">
              <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)] mb-2">
                Top reasons symbols were filtered out
              </div>
              <ul className="space-y-1">
                {topRejects.map(([code, count]) => (
                  <li
                    key={code}
                    className="flex items-start gap-2 text-[12px]"
                    data-testid={`matrix-health-reject-${code}`}
                  >
                    <span className="font-mono tabular-nums text-[var(--muted-text)] w-12 text-right">
                      {count.toLocaleString()}
                    </span>
                    <span className="text-[var(--app-text)]">{prettyReason(code)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div
            className="rounded-md border border-[var(--card-border)] bg-[var(--card-bg)] p-3 text-[12px]"
            data-testid="signal-health-summary-completeness"
          >
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">
                Data completeness
              </span>
              <ToneBadge tone={completenessTone}>
                {completeEnough ? "Complete" : "Limited"}
              </ToneBadge>
            </div>
            <div className="text-[var(--muted-text)] leading-snug">
              {completeEnough
                ? "All required providers are fresh; today's Signal output reflects the full filter pipeline."
                : blockers.length > 0
                ? `Some data sources are not fully fresh: ${blockers
                    .map((b) => `${b.provider} ${prettyStatus(b.status).toLowerCase()}`)
                    .join(", ")}. Today's Signal output may be partial.`
                : "Signal is operating in a non-LIVE state; review the provider grid above for details."}
            </div>
          </div>

          {/* Advanced Diagnostics — admin-only when localStorage flag set.
              Customers see a brief note; operators with the flag see the raw
              JSON payloads collapsed by default. */}
          {adminMode ? (
            <section
              className="border-t border-[var(--card-border)] pt-3"
              data-testid="signal-advanced-diagnostics"
            >
              <button
                type="button"
                onClick={() => setAdvOpen(!advOpen)}
                className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-[var(--muted-text)] hover:text-[var(--app-text)]"
                data-testid="signal-advanced-diagnostics-toggle"
              >
                {advOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                Advanced Diagnostics
                <ToneBadge tone="neutral">admin-only</ToneBadge>
              </button>
              {!advOpen && (
                <p className="text-[11px] text-[var(--muted-text)] mt-2">
                  Raw provider-health and candidates payloads for debugging. Collapsed by default.
                </p>
              )}
              {advOpen && (
                <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)] font-bold mb-2">
                      provider-health
                    </div>
                    <pre
                      className="text-[10px] font-mono bg-[var(--card-bg)] border border-[var(--card-border)] rounded p-3 overflow-x-auto max-h-96 text-[var(--app-text)]"
                      data-testid="signal-advanced-diagnostics-health"
                    >
                      {health ? JSON.stringify(health, null, 2) : "no data"}
                    </pre>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)] font-bold mb-2">
                      candidates ({candidates?.candidate_count || 0})
                    </div>
                    <pre
                      className="text-[10px] font-mono bg-[var(--card-bg)] border border-[var(--card-border)] rounded p-3 overflow-x-auto max-h-96 text-[var(--app-text)]"
                      data-testid="signal-advanced-diagnostics-candidates"
                    >
                      {candidates
                        ? JSON.stringify(
                            {
                              status: candidates.status,
                              as_of_utc: candidates.as_of_utc,
                              score_version: candidates.score_version,
                              universe_size: candidates.universe_size,
                              candidate_count: candidates.candidate_count,
                              rejected_counts: candidates.rejected_counts,
                              provider_snapshot: candidates.provider_snapshot,
                            },
                            null,
                            2,
                          )
                        : "no data"}
                    </pre>
                  </div>
                </div>
              )}
            </section>
          ) : (
            <div
              className="border-t border-[var(--card-border)] pt-3 text-[11px] text-[var(--muted-text)]"
              data-testid="signal-advanced-diagnostics-locked"
            >
              Advanced Diagnostics (raw provider-health and candidates payloads) is restricted to operators.
              Operators can enable it by setting <code className="font-mono">adversyn-admin-diagnostics=true</code> in browser storage.
            </div>
          )}
        </div>
      )}
    </section>
  );
}
